import React, { createContext, useCallback, useContext, useState } from "react";
import { addMinutes, addMonths } from "date-fns";

import cookie from "react-cookies";
import generateID from "utils/generateID";
import { getHasInsuranceAuthorization } from "api/insurance";
import { useGetGeneratedSchedules } from "hooks/api/clinicians/useGetGeneratedSchedules";
import useGetMatchingSessions from "hooks/api/clinicians/useGetMatchingSessions";
import { formatRepeatDays } from "components/EventScheduling/EventSchedulingUtils";

const MatchingContext = createContext(null);

const getDuration = (arr) =>
  arr.reduce(
    (acc, { call_duration, repeat_days }) =>
      acc + call_duration * (repeat_days.split(",").length || 1),
    0
  );

const repeatDaysUTCtoLocal = (repeatDays, scheduledDate) => {
  const iso8601Day = [7, 1, 2, 3, 4, 5, 6];
  const date = new Date(scheduledDate);
  const diff = iso8601Day[date.getDay()] - iso8601Day[date.getUTCDay()];
  return repeatDays
    .split(",")
    .map((day) => Number(day) + diff)
    .join(",");
};

export const MatchingProvider = ({ children }) => {
  const userId = cookie.load("userId");
  const [selectedClinician, setSelectedClinician] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  // This state is used to track if no matches were created,
  // that is different from the empty queue
  // (which means that all matches were approved/set as tentative)
  const [noMatchesCreated, setNoMatchesCreated] = useState(false);

  // This state is used to track the last time the schedule was modified
  const [modifiedAt, setModifiedAt] = useState(null);

  // This state is used to display the Refresh Dialog when a schedule has changed
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { recurrentEventsParent, isEventsLoading, refetchRecurrentEvents } = useGetMatchingSessions(
    {
      clinicianId: selectedClinician,
      clientIds: [selectedClient],
    }
  );

  const { allEvents, nonWorkingHours, singleEvents } = recurrentEventsParent ?? {};

  const {
    generatedSchedules: rawGeneratedSchedules,
    generatedSchedulesLoading,
    refetch,
  } = useGetGeneratedSchedules();

  const [generatedSchedules, setGeneratedSchedules] = useState([]);

  React.useEffect(() => {
    if (!rawGeneratedSchedules) return;

    let selectedClientStillExists = false;

    // Create deep copy to be able to inject conflicts
    const clonedSchedules = rawGeneratedSchedules?.data.map((clinician) => {
      // Calculate current_case_load for each clinician
      const currentCaseLoad = allEvents?.reduce((acc, e) => {
        return acc + (e.call_duration * e.repeat_days.split(",").length) / 60;
      }, 0);

      const newCaseLoad =
        clinician.client_schedules?.reduce(
          (acc, { eventsData }) => acc + getDuration(eventsData),
          0
        ) / 60;

      return {
        ...clinician,
        current_case_load: currentCaseLoad,
        new_case_load: newCaseLoad,
        client_schedules: clinician.client_schedules?.map((client) => {
          if (client.client_id === selectedClient) {
            selectedClientStillExists = true;
          }

          return {
            ...client,
            eventsData: client.eventsData.map((event) => ({
              ...event,
              repeat_days: repeatDaysUTCtoLocal(event.repeat_days, new Date(event.scheduled_date)),
              id: generateID(event.client_id),
            })),
          };
        }),
      };
    });

    const sortedSchedules = clonedSchedules.sort((a, b) => {
      // higher new_case_load first
      if (b.new_case_load !== a.new_case_load) {
        return b.new_case_load - a.new_case_load;
      }
    });

    if (!selectedClientStillExists) {
      setSelectedClient(null);
    }

    setGeneratedSchedules(sortedSchedules);
    setNoMatchesCreated(rawGeneratedSchedules?.matches_count === 0);
  }, [rawGeneratedSchedules, allEvents]);

  // This effect is used to show the refresh dialog when the schedule has changed
  React.useEffect(() => {
    const showRefreshDialog =
      modifiedAt &&
      modifiedAt !== rawGeneratedSchedules?.modified_at &&
      userId != rawGeneratedSchedules?.modified_by;

    // If the schedule was modified by another user, show the refresh dialog
    if (showRefreshDialog) {
      setIsRefreshing(true);
    }

    setModifiedAt(rawGeneratedSchedules?.modified_at);
  }, [rawGeneratedSchedules?.modified_at]);

  const getClinicianSchedule = useCallback(
    (clientId) => {
      if (!generatedSchedules || !selectedClinician) return;

      const clinicianScheduleData = generatedSchedules.find(
        (clinician) => clinician.clinician_id == selectedClinician
      );

      if (!clinicianScheduleData) return;
      let allEvents = [];
      let clientIds = [];

      // If clientId is provided, filter to get only that client's events
      if (clientId) {
        const clientSchedule = clinicianScheduleData.client_schedules?.find(
          (client) => client.client_id == clientId
        );

        if (!clientSchedule) return [];

        clientIds = [clientId];
        allEvents = clientSchedule.eventsData.map((event) => ({
          ...event,
          client_name: clientSchedule.client_name,
          clinician_name: clinicianScheduleData.clinician_name,
          clinician_id: clinicianScheduleData.clinician_id,
        }));
      }
      // Otherwise, collect all events from all clients
      else {
        clinicianScheduleData.client_schedules?.forEach((clientSchedule) => {
          const events = clientSchedule.eventsData.map((event) => ({
            ...event,
            client_name: clientSchedule.client_name,
            clinician_id: clinicianScheduleData.clinician_id,
            clinician_name: clinicianScheduleData.clinician_name,
          }));
          allEvents = [...allEvents, ...events];
          clientIds.push(clientSchedule.client_id);
        });
      }
      return { allEvents, clientIds };
    },
    [generatedSchedules, selectedClinician]
  );

  const generateApproveSchedulePayload = useCallback(
    async ({ clientId, setTentative = false }) => {
      const clinicianSchedule = getClinicianSchedule(clientId);
      if (!clinicianSchedule) return [];
      const { allEvents, clientIds } = clinicianSchedule;
      let clientAuthorizations = {};

      const fetchClientAuthorization = async (clientId) => {
        if (clientAuthorizations[clientId] !== undefined) {
          return clientAuthorizations[clientId];
        }
        try {
          const response = await getHasInsuranceAuthorization(clientId);
          clientAuthorizations[clientId] = response?.data?.hasInsuranceAuthorization;
        } catch (error) {
          console.error("Failed to fetch insurance authorization", error);
          clientAuthorizations[clientId] = false;
        }
      };

      await Promise.all(clientIds.map((clientId) => fetchClientAuthorization(clientId)));

      const eventPromises = allEvents.map(async (event) => {
        const hasAuthorization = clientAuthorizations[event.client_id];
        return {
          startDate: event.scheduled_date,
          endDate: addMinutes(new Date(event.scheduled_date), event.call_duration),
          clinicianUserId: event.clinician_user_id,
          secondaryClinicianUserId: null,
          billingType: event.billing_type,
          offPlatform: false,
          repeatDays: formatRepeatDays(event.repeat_days),
          overrideSchedule: true,
          timeUnit: "weeks",
          timeInterval: 1,
          isRepeating: true,
          isTentative: setTentative,
          clientId: event.client_id,
          repeatEndType: hasAuthorization ? "current_auth_period" : "until_date",
          repeatUntilDate: hasAuthorization ? null : addMonths(new Date(event.scheduled_date), 6),
          source: event.source,
        };
      });

      const payload = await Promise.all(eventPromises);
      return { payload, clientIds };
    },
    [getClinicianSchedule]
  );

  const value = React.useMemo(
    () => ({
      generateApproveSchedulePayload,
      generatedSchedules,
      generatedSchedulesLoading,
      getClinicianSchedule,
      isEventsLoading,
      nonWorkingHours,
      selectedClient,
      selectedClinician,
      recurringEventData: allEvents,
      refetchGeneratedSchedules: refetch,
      refetchRecurrentEvents,
      setGeneratedSchedules,
      setSelectedClient,
      setSelectedClinician,
      singleEvents,
      noMatchesCreated,
      isRefreshing,
    }),
    [
      generateApproveSchedulePayload,
      generatedSchedules,
      generatedSchedulesLoading,
      getClinicianSchedule,
      isEventsLoading,
      nonWorkingHours,
      selectedClient,
      selectedClinician,
      allEvents,
      refetch,
      refetchRecurrentEvents,
      setGeneratedSchedules,
      setSelectedClient,
      setSelectedClinician,
      singleEvents,
      noMatchesCreated,
      isRefreshing,
    ]
  );

  return (
    <MatchingContext.Provider value={value}>
      {typeof children === "function" ? children(value) : children}
    </MatchingContext.Provider>
  );
};

export const useMatching = () => {
  const context = useContext(MatchingContext);
  if (!context) {
    throw new Error("useMatching must be used within a MatchingProvider");
  }
  return context;
};
